"use client";

import { useState } from "react";
import { Box as MantineBox, Text as MantineText } from "@mantine/core";

import styles from "../styles.module.scss";

import {
  formatNumber,
  type BubbleVendor,
  type PriceVendorEntry,
  type VendorEntry,
  type ViolinBrand,
} from "./reportData";

export type { BubbleData, PriceVendorEntry, VendorEntry } from "./reportData";

// ─── Types ────────────────────────────────────────────────────────────────────

export const BUBBLE_COLORS = [
  "#1500ff",
  "#6600aa",
  "#0891b2",
  "#059669",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0284c7",
  "#16a34a",
  "#ca8a04",
  "#be123c",
  "#4338ca",
  "#0369a1",
  "#15803d",
  "#b45309",
  "#991b1b",
  "#5b21b6",
  "#0c4a6e",
  "#166534",
  "#78350f",
];

export function BubbleTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: BubbleVendor }>;
}) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <MantineBox
      style={{
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.1)",
        borderRadius: 6,
        padding: "8px 14px",
        fontSize: 12,
      }}
    >
      <MantineBox style={{ fontWeight: 700, marginBottom: 4 }}>{d.name}</MantineBox>
      <MantineBox>Volume: {d.x.toLocaleString()} listings</MantineBox>
      <MantineBox>Median: ${d.y.toLocaleString()}</MantineBox>
      <MantineBox>IQR: ${d.z.toLocaleString()}</MantineBox>
    </MantineBox>
  );
}
// ─── Box plot helpers ─────────────────────────────────────────────────────────

function pctile(sorted: number[], p: number): number {
  const i = (sorted.length - 1) * p;
  const lo = Math.floor(i);
  return sorted[lo] + (sorted[Math.ceil(i)] - sorted[lo]) * (i - lo);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const VENDOR_COLS: { key: "marketplace" | "franchise" | "independent"; label: string }[] = [
  { key: "marketplace", label: "MARKETPLACES" },
  { key: "franchise", label: "CHAINS" },
  { key: "independent", label: "INDEPENDENT STORES" },
];

export function VendorTable({
  data,
}: {
  data: { marketplace: VendorEntry[]; franchise: VendorEntry[]; independent: VendorEntry[] };
}) {
  return (
    <MantineBox className={styles.vendorTable}>
      <MantineBox className={styles.vendorTableHeader}>
        {VENDOR_COLS.map((c) => (
          <MantineBox key={c.key} className={styles.vendorTableCol}>
            <MantineText span className={styles.colLabel}>
              {c.label}
            </MantineText>
            {data[c.key].slice(0, 10).map((v: VendorEntry) => (
              <MantineBox key={v.rank} className={styles.vendorRow}>
                <MantineText span className={styles.vendorRank}>
                  {v.rank}
                </MantineText>
                <MantineText span className={styles.vendorName}>
                  {v.name}
                </MantineText>
                <MantineText span className={styles.vendorCount}>
                  {formatNumber(v.count)}
                </MantineText>
              </MantineBox>
            ))}
          </MantineBox>
        ))}
      </MantineBox>
    </MantineBox>
  );
}

export function PriceVendorTable({
  data,
}: {
  data: {
    marketplace: PriceVendorEntry[];
    franchise: PriceVendorEntry[];
    independent: PriceVendorEntry[];
  };
}) {
  return (
    <MantineBox className={styles.vendorTable}>
      <MantineBox className={styles.vendorTableHeader}>
        {VENDOR_COLS.map((c) => (
          <MantineBox key={c.key} className={styles.vendorTableCol}>
            <MantineText span className={styles.colLabel}>
              {c.label}
            </MantineText>
            {data[c.key].map((v: PriceVendorEntry) => (
              <MantineBox key={v.rank} className={styles.vendorRow}>
                <MantineText span className={styles.vendorRank}>
                  {v.rank}
                </MantineText>
                <MantineText span className={styles.vendorName}>
                  {v.name}
                </MantineText>
                <MantineText span className={styles.vendorCount}>
                  ${v.medianPrice.toLocaleString()}
                </MantineText>
              </MantineBox>
            ))}
          </MantineBox>
        ))}
      </MantineBox>
    </MantineBox>
  );
}

// ─── Box & whisker chart (custom SVG) ────────────────────────────────────────

type BoxStats = {
  vendor: string;
  wMin: number;
  q1: number;
  med: number;
  q3: number;
  wMax: number;
  iqr: number;
  n: number;
};

export function ViolinChart({ data, color }: { data: ViolinBrand[]; color: string }) {
  const [hovered, setHovered] = useState<BoxStats | null>(null);

  const W = 560;
  const ML = 136;
  const MR = 80; // extra right margin for tooltip
  const MT = 16;
  const MB = 52;
  const ROW = 44;
  const BOX_H = 16;
  const chartW = W - ML - MR;
  const totalH = data.length * ROW + MT + MB;

  // Compute per-row stats first so we can derive a sensible x-axis range
  const stats: (BoxStats | null)[] = data.map((d) => {
    if (d.prices.length < 2) return null;
    const sorted = [...d.prices].sort((a, b) => a - b);
    const q1 = pctile(sorted, 0.25);
    const med = pctile(sorted, 0.5);
    const q3 = pctile(sorted, 0.75);
    const iqr = q3 - q1;
    // Tukey fences: whiskers extend to last data point within 1.5×IQR of box
    const lowerFence = q1 - 1.5 * iqr;
    const upperFence = q3 + 1.5 * iqr;
    const wMin = sorted.find((v) => v >= lowerFence) ?? sorted[0];
    const wMax = [...sorted].reverse().find((v) => v <= upperFence) ?? sorted[sorted.length - 1];
    return { vendor: d.brand, wMin, q1, med, q3, wMax, iqr, n: sorted.length };
  });

  const validStats = stats.filter((s): s is BoxStats => s !== null);
  if (validStats.length === 0) return null;

  // x-axis driven by Tukey-clipped whisker maxes — no single outlier blows out the scale
  const pMin = 0;
  const pMax = Math.max(...validStats.map((s) => s.wMax)) * 1.1;
  const xScale = (p: number) => ((p - pMin) / (pMax - pMin)) * chartW;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => pMin + t * (pMax - pMin));

  const fmt = (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v)));

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${totalH}`}
      onMouseLeave={() => setHovered(null)}
      style={{ overflow: "visible" }}
    >
      <g transform={`translate(${ML},${MT})`}>
        {ticks.map((t) => (
          <g key={t} transform={`translate(${xScale(t).toFixed(1)},0)`}>
            <line y1={0} y2={data.length * ROW} stroke="rgba(0,0,0,0.08)" strokeDasharray="3 3" />
            <text
              y={data.length * ROW + 18}
              textAnchor="middle"
              fontSize={11}
              fontWeight={600}
              fill="#555"
              letterSpacing="0.02em"
            >
              {fmt(t)}
            </text>
          </g>
        ))}

        {/* X axis label */}
        <text
          x={chartW / 2}
          y={data.length * ROW + 40}
          textAnchor="middle"
          fontSize={10}
          fontWeight={700}
          fill="#999"
          letterSpacing="0.1em"
        >
          PRICE (USD)
        </text>

        {/* Y axis label */}
        <text
          textAnchor="middle"
          fontSize={10}
          fontWeight={700}
          fill="#999"
          letterSpacing="0.1em"
          transform={`rotate(-90) translate(${-(data.length * ROW) / 2}, ${-ML + 16})`}
        >
          VENDOR
        </text>

        {data.map((d, i) => {
          const s = stats[i];
          if (!s) return null;
          const cy = i * ROW + ROW / 2;
          const capH = BOX_H * 0.45;
          const isHov = hovered?.vendor === d.brand;

          return (
            <g
              key={d.brand}
              onMouseEnter={() => setHovered(s)}
              onClick={() => setHovered((prev) => (prev?.vendor === d.brand ? null : s))}
              style={{ cursor: "default" }}
            >
              {/* Invisible hit area for the whole row */}
              <rect x={-ML} y={cy - ROW / 2} width={W} height={ROW} fill="transparent" />
              <text
                x={-8}
                y={cy + 4}
                textAnchor="end"
                fontSize={10}
                fill={isHov ? color : "#555"}
                fontWeight={isHov ? 600 : 400}
              >
                {d.brand}
              </text>
              {/* Whisker line */}
              <line
                x1={xScale(s.wMin).toFixed(1)}
                y1={cy}
                x2={xScale(s.wMax).toFixed(1)}
                y2={cy}
                stroke={color}
                strokeWidth={1.5}
                strokeOpacity={0.35}
              />
              {/* Whisker caps */}
              <line
                x1={xScale(s.wMin).toFixed(1)}
                y1={cy - capH}
                x2={xScale(s.wMin).toFixed(1)}
                y2={cy + capH}
                stroke={color}
                strokeWidth={1.5}
                strokeOpacity={0.55}
              />
              <line
                x1={xScale(s.wMax).toFixed(1)}
                y1={cy - capH}
                x2={xScale(s.wMax).toFixed(1)}
                y2={cy + capH}
                stroke={color}
                strokeWidth={1.5}
                strokeOpacity={0.55}
              />
              {/* IQR box */}
              <rect
                x={xScale(s.q1).toFixed(1)}
                y={cy - BOX_H / 2}
                width={Math.max(xScale(s.q3) - xScale(s.q1), 1).toFixed(1)}
                height={BOX_H}
                fill={color}
                fillOpacity={isHov ? 0.28 : 0.18}
                stroke={color}
                strokeWidth={1.5}
              />
              {/* Median line */}
              <line
                x1={xScale(s.med).toFixed(1)}
                y1={cy - BOX_H / 2}
                x2={xScale(s.med).toFixed(1)}
                y2={cy + BOX_H / 2}
                stroke={color}
                strokeWidth={2.5}
              />
            </g>
          );
        })}

        {/* Hover tooltip */}
        {hovered &&
          (() => {
            const i = data.findIndex((d) => d.brand === hovered.vendor);
            if (i < 0) return null;
            const cy = i * ROW + ROW / 2;
            const TW = 160,
              TH = 84,
              PAD = 12;
            // Place inside chart, prefer right side, flip left if it would overflow
            const rightX = chartW - TW - 8;
            const tx = Math.max(0, rightX);
            const ty = Math.min(Math.max(0, cy - TH / 2), data.length * ROW - TH);
            return (
              <g style={{ pointerEvents: "none" }}>
                <rect
                  x={tx}
                  y={ty}
                  width={TW}
                  height={TH}
                  rx={6}
                  fill="white"
                  stroke="rgba(0,0,0,0.12)"
                  strokeWidth={1}
                  filter="drop-shadow(0 2px 6px rgba(0,0,0,0.10))"
                />
                <text x={tx + PAD} y={ty + PAD + 12} fontSize={12} fontWeight={700} fill="#111">
                  {hovered.vendor}
                </text>
                <text x={tx + PAD} y={ty + PAD + 28} fontSize={11} fill="#333">
                  {`Median $${fmt(hovered.med)}`}
                </text>
                <text x={tx + PAD} y={ty + PAD + 44} fontSize={11} fill="#333">
                  {`IQR $${fmt(hovered.q1)}–$${fmt(hovered.q3)}`}
                </text>
                <text x={tx + PAD} y={ty + PAD + 60} fontSize={11} fill="#333">
                  {`n = ${hovered.n.toLocaleString()}`}
                </text>
              </g>
            );
          })()}
      </g>
    </svg>
  );
}
